import { last } from 'lodash';
import componentServices from './componentServices';
import * as history from 'history';
import { pathToRegexp } from 'path-to-regexp';
import Emitter from './emitter';
import store from './store';
import globalRelay from './globalRelay';

const createBrowserHistory = history.createBrowserHistory;
const createMemoryHistory = history.createMemoryHistory;
const hasWindow = typeof window !== 'undefined';
const windowLoc = hasWindow? window.location: undefined;

let _createHistory;
let _navConfirmHandler;
let _unblockHistory;

if (hasWindow) {
	_createHistory = createBrowserHistory;
}
else { // NOTE: to support testing
	_createHistory = createMemoryHistory;
}

const _history = _createHistory({
	getUserConfirmation(message, callback) {
		if (_navConfirmHandler) {
			_navConfirmHandler(message, callback);
		}
		else {
			callback(confirm(message)); // eslint-disable-line no-alert
		}
	}
});

const _emitter = new Emitter();
let _modePathPrefix;

function getModePathPrefix() {
	if(_modePathPrefix === undefined && windowLoc) {
		_modePathPrefix = globalRelay.clientMode === 'Default'? '': `/${windowLoc.pathname.split('/')[1]}`;
	}

	return _modePathPrefix || '';
}

function matchRoute(route, location) {
	// cache the regex and keys on the route object
	if (!route.regex) {
		route.pathKeys = [];
		route.regex = pathToRegexp(`${getModePathPrefix()}${route.path}`, route.pathKeys);
	}

	const match = route.regex.exec(location.pathname);
	if (!match) { return null; }

	const params = {};
	for (let i = 1; i < match.length; i++) {
		params[route.pathKeys[i - 1].name] = match[i];
	}

	return { route: route, location: location, params: params };
}

class Router {
	private _routes: any[];
	private _currentState;

	constructor() {
		this._routes = [];

		componentServices.set('router', this);

		_history.listen((location, action) => {
			this.setLocation(location, action);
		});
	}

	static get EVENT_ROUTE_CHANGE() {
		return 'route.change';
	}

	get history() {
		return _history;
	}

	get currentState() {
		return this._currentState;
	}

	requireModule(moduleKey: string) {
		return function ({ securityContext }) {
			const user = securityContext.getCurrentUser();

			return !!user && user.hasModule(moduleKey);
		};
	}

	requireUser(userTest?, redirect: string | ((next: any) => string) = '/') {
		return function ({ next, replace }) {
			const { securityContext } = componentServices;
			const user = securityContext?.getCurrentUser();

			if (!user) {
				replace('/login');
				// cancel the route transition
				return false;
			}
			else if (userTest && !userTest({ securityContext, next })) {
				replace(typeof redirect === 'string' ? redirect : redirect(next));
				// cancel the route transition
				return false;
			}
		};
	}

	addRoutes(value) {
		this._routes = this._routes.concat(value);
	}

	isActive(...args) {
		if (args.length === 0) { throw new Error('No args passed to router.isActive'); }

		if (args.length > 1 || args[0] instanceof Array) {
			const segments = this.getPathSegments();
			const argSegments = (args[0] instanceof Array) ? args[0] : Array.prototype.slice.call(args);

			if (argSegments.length > segments.length) { return false; }

			let test;
			let segment;

			for (let i = 0, l = argSegments.length; i < l; i++) {
				test = argSegments[i];
				segment = segments[i];
				// WARNING: arrays with value other than string or RegExp could return false positive
				if (test !== '*' && ((test instanceof RegExp && !test.test(segment)) || (typeof test === 'string' && test !== segment))) {
					return false;
				}
			}

			return true;
		}
		else {
			const spec = args[0];

			if (typeof spec === 'string') {
				const re = pathToRegexp(spec);
				return !!re.exec(_history.location.pathname);
			}
			else if (spec instanceof RegExp) {
				return spec.test(_history.location.pathname);
			}
		}

		return false;
	}

	back(e) {
		if (e) { e.preventDefault(); }
		_history.goBack();
	}

	pushHistory(path, state?) {
		_history.push({ pathname: `${getModePathPrefix()}${path}`, state });
	}

	replaceHistory(path) {
		_history.replace({ pathname: `${getModePathPrefix()}${path}` });
	}

	getPathSegments() {
		const rootIndex = getModePathPrefix()? 2: 1;

		return _history.location.pathname.split('/').slice(rootIndex);
	}

	getLastPathSegment() {
		return last(this.getPathSegments());
	}

	pushPathSegment(newSegment) {
		const segments = this.getPathSegments();

		segments.push(newSegment);
		return '/' + segments.join('/');
	}

	pushPathSegments(...args) {
		// allow string params or an array
		const argArray = typeof args[0] === 'string' ? args : args[0];
		// the super high start index ensures we splice at the end
		return this.splicePathSegments(1000000, 0, ...argArray);
	}

	popPathSegment() {
		const segments = this.getPathSegments();

		segments.pop();
		return '/' + segments.join('/');
	}

	splicePathSegments(...args) {
		const segments = this.getPathSegments();

		segments.splice(...args);
		return '/' + segments.join('/');
	}

	replaceLastPathSegment(newSegment) {
		const segments = this.getPathSegments();

		segments.splice(segments.length - 1, 1, newSegment);
		return '/' + segments.join('/');
	}

	replacePathSegmentAt(index, newSegment, truncate, segments?) {
		segments = segments || this.getPathSegments();

		segments.splice(index, 1, newSegment);
		if (truncate && index < (segments.length - 1)) {
			segments = segments.slice(0, index + 1);
		}
		return '/' + segments.join('/');
	}

	setRouteLeaveConfirmation(leaveHandler, confirmHandler) {
		if (!leaveHandler && _unblockHistory) {
			_unblockHistory();
			_navConfirmHandler = undefined;
			_unblockHistory = undefined;
		}
		else {
			if(_unblockHistory) {
				_unblockHistory();
			}
			_navConfirmHandler = confirmHandler;
			_unblockHistory = _history.block(() => {
				return leaveHandler();
			});
		}
	}

	onRouteChange(handler) {
		_emitter.on(Router.EVENT_ROUTE_CHANGE, handler);
	}

	setLocation(location, action) {
		if(globalRelay.clientMode !== 'Default' && windowLoc && windowLoc.pathname === `${getModePathPrefix()}`) {
			windowLoc.replace(`${getModePathPrefix()}/`);
		}

		const currentState = this._currentState;
		let matchedState: any = null;
		for (const route of this._routes) {
			matchedState = matchRoute(route, location);
			if (matchedState) {
				break;
			}
		}

		if (matchedState) {
			let triggerChange = true;
			
			// this ensures that the current user is properly set for onEnter checks
			componentServices.set('store', store());
			// componentServices.set('securityContext', securityContext(() => componentServices.store));

			if (typeof matchedState.route.onEnter === 'function') {
				const enterResult = matchedState.route.onEnter({ current: currentState, next: matchedState, replace: (path, ...args) => _history.replace(`${getModePathPrefix()}${path}`, ...args) });
				if (enterResult === false) {
					triggerChange = false;
				}
			}

			if (triggerChange) {
				this._currentState = matchedState;
				componentServices.set('route', matchedState.route).set('routeParams', matchedState.params);
				_emitter.trigger(Router.EVENT_ROUTE_CHANGE, { location: location, action: action, previous: currentState, current: this._currentState });
			}
		}
		else if (typeof window !== 'undefined') {
			console.warn('No route match found: ' + location.pathname); // eslint-disable-line no-console
		}
	}
}

export default new Router();